import { useState, useEffect, useMemo, useRef } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/Button";
import { SelectRoot } from "@/components/Select";
import { ProgressBar } from "@/components/ProgressBar";
import { Text } from "@/components/Typography";
import { FormattedText } from "@/components/FormattedText";
import { PABCard } from "@/services/api";
import {
  usePABSessionDetailed,
  useAdvisorMeetings,
  useAdvisorPastMeetings,
} from "@/hooks/useDashboardStats";
import { MessagesTab } from "./MessagesTab";
import { getGoalStatusColor, getGoalStatusLabel, getTaskStatusColor, getTaskStatusLabel } from "@/utils/goalsUtils";
import { COMPLETED_TASK_STATUSES } from "@/constants/goals";
import { formatDate, validateSafeUrl } from "@/utils/util";
import { formatDisplayDate } from "@/utils/formatDisplayDate";
import { Heading } from "@/components/Typography";

export interface AdvisorGoalsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  pab?: PABCard;
  pabs: PABCard[];
  onPABChange?: (pabSessionId: number) => void;
  initialTab?: "goals" | "sessions" | "messages";
  session?: { pabSessionId: number }; // Add session prop for MessagesTab
}

export const AdvisorGoalsDrawer = ({
  isOpen,
  onClose,
  pab,
  pabs,
  onPABChange,
  initialTab = "messages",
}: AdvisorGoalsDrawerProps) => {
  const [selectedGoal, setSelectedGoal] = useState(0);
  const [selectedPABId, setSelectedPABId] = useState(pab?.pabSessionId || 0);
  // Redirect "sessions" tab to "messages" since sessions route is now available
  const normalizedInitialTab = initialTab === "sessions" ? "messages" : initialTab;
  const [activeTab, setActiveTab] = useState<"goals" | "sessions" | "messages">(normalizedInitialTab);
  const [selectedPABValues, setSelectedPABValues] = useState<string[]>([]);
  const previousIsOpen = useRef(false);

  // Create options for MultiSelect from PABs data
  const pabOptions = useMemo(() => {
    return pabs.map((pabItem) => ({
      label: `${pabItem.achiever.fullName}`,
      value: pabItem.pabSessionId.toString(),
    }));
  }, [pabs]);

  // Convert selectedPABValues to selectedPABIds for filtering
  const selectedPABIds = useMemo(() => {
    return selectedPABValues.map((value) => Number(value));
  }, [selectedPABValues]);

  // Initialize selectedPABId and selectedPABValues when pab prop changes
  useEffect(() => {
    if (pab?.pabSessionId) {
      if (selectedPABId !== pab.pabSessionId) {
        setSelectedPABId(pab.pabSessionId);
      }
      // Always update selectedPABValues when pab prop is provided
      setSelectedPABValues([pab.pabSessionId.toString()]);
    } else if (selectedPABId && selectedPABId > 0 && selectedPABValues.length === 0) {
      // If no pab prop but we have a selectedPABId, initialize selectedPABValues
      setSelectedPABValues([selectedPABId.toString()]);
    }
  }, [pab?.pabSessionId, selectedPABId, selectedPABValues.length]);

  // Update activeTab only when drawer is freshly opened (not already open)
  useEffect(() => {
    if (isOpen && !previousIsOpen.current) {
      // Drawer is opening from closed state - set the initial tab
      setActiveTab(initialTab);
      // Initialize selectedPABValues when drawer opens if we have a valid PAB
      if (pab?.pabSessionId) {
        setSelectedPABId(pab.pabSessionId);
        setSelectedPABValues([pab.pabSessionId.toString()]);
      } else if (selectedPABId && selectedPABId > 0) {
        setSelectedPABValues([selectedPABId.toString()]);
      }
    }
    previousIsOpen.current = isOpen;
  }, [isOpen, initialTab, pab?.pabSessionId, selectedPABId]);

  // Initialize selectedPABValues when drawer opens on Sessions tab
  useEffect(() => {
    if (isOpen && activeTab === "sessions" && pabOptions.length > 0) {
      // If we have a specific PAB selected, use only that one for sessions
      if (pab?.pabSessionId && initialTab === "sessions") {
        setSelectedPABValues([pab.pabSessionId.toString()]);
      }
      // Otherwise, if no values are selected, show all PABs
      else if (selectedPABValues.length === 0) {
        setSelectedPABValues(pabOptions.map((option) => option.value));
      }
    }
  }, [isOpen, activeTab, pabOptions, selectedPABValues.length, pab?.pabSessionId, initialTab]);

  // Ensure selectedPABValues is set when drawer opens with a valid selectedPABId
  // This is especially important for the Goals tab
  useEffect(() => {
    if (isOpen && selectedPABId && selectedPABId > 0) {
      const selectedPABIdStr = selectedPABId.toString();
      // For Goals tab, we need exactly one PAB selected
      if (activeTab === "goals" && !selectedPABValues.includes(selectedPABIdStr)) {
        setSelectedPABValues([selectedPABIdStr]);
      }
      // For other tabs, ensure the selected PAB is in the list if it's not already
      else if (activeTab !== "goals" && !selectedPABValues.includes(selectedPABIdStr)) {
        // Don't override if we already have values (e.g., all PABs for Sessions tab)
        if (selectedPABValues.length === 0) {
          setSelectedPABValues([selectedPABIdStr]);
        }
      }
    }
  }, [isOpen, activeTab, selectedPABId, selectedPABValues]);

  // Fetch PAB session detailed data
  const { data: pabSessionData, isLoading, error } = usePABSessionDetailed(selectedPABId || null);

  // Fetch advisor meetings data
  const { data: meetings, isLoading: meetingsLoading, error: meetingsError } = useAdvisorMeetings();
  const {
    data: pastMeetings,
    isLoading: pastMeetingsLoading,
    error: pastMeetingsError,
  } = useAdvisorPastMeetings();

  // Memoize goals data to avoid unnecessary recalculations
  const currentGoals = useMemo(() => {
    return pabSessionData?.okrs || [];
  }, [pabSessionData?.okrs]);

  // Reset goal selection when PAB session changes
  useEffect(() => {
    if (currentGoals.length > 0) {
      setSelectedGoal(0);
    }
  }, [currentGoals]);

  if (!isOpen) return null;

  const handlePABSelect = (pabSessionId: number) => {
    // Only call API if different PAB session is selected
    if (pabSessionId !== selectedPABId) {
      setSelectedPABId(pabSessionId);
      onPABChange?.(pabSessionId);
    }
  };

  const currentGoal = currentGoals[selectedGoal];

  // Calculate tasks completion — use same statuses as achiever goals section
  const completedTasks =
    currentGoal?.tasks?.filter((task) =>
      COMPLETED_TASK_STATUSES.includes(task.status as typeof COMPLETED_TASK_STATUSES[number])
    ).length || 0;
  const totalTasks = currentGoal?.tasks?.length || 0;

  // Helper function to format meeting date and time
  const formatMeetingDateTime = (startTime: string, endTime: string) =>
    formatDisplayDate(startTime, { end: endTime, length: "long" });

  return (
    <div className="fixed inset-0 z-50 overflow-hidden" style={{ margin: 0, padding: 0 }}>
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity z-40"
        onClick={onClose}
      />

      {/* Drawer - Exact 360px width from Figma */}
      <div
        className="absolute right-0 top-0 h-full bg-white shadow-xl transform transition-transform duration-300 ease-in-out overflow-hidden z-50"
        style={{ width: "360px" }}
      >
        {/* Scrollable Content - 16px padding matching Figma */}
        <div className="h-full overflow-y-auto p-4">
          <div className="w-full max-w-[328px] mx-auto space-y-4 relative">
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-0 right-0 p-2 hover:bg-gray-100 rounded-full transition-colors z-10"
            >
              <X size={20} className="text-gray-500" />
            </button>

            {/* Tabs matching Figma design */}
            <div className="relative">
              <div className="flex">
                {(["goals", "sessions", "messages"] as const)
                  .filter((tab) => tab !== "sessions") // Hide sessions tab - sessions route is now available
                  .map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`px-2 py-3 text-sm font-medium capitalize cursor-pointer ${
                        activeTab === tab
                          ? "text-primary-600 border-b-2 border-primary-600"
                          : "text-gray-500 hover:text-gray-700"
                        }`}
                    >
                      {tab}
                    </button>
                  ))}
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-px bg-gray-200" />
            </div>

            {/* PAB Selector - Single Select for all tabs */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Achiever:</label>

              <SelectRoot
                name="pabSession"
                value={selectedPABId ? selectedPABId.toString() : ""}
                onValueChange={(value) => {
                  const numValue = Number(value);
                  handlePABSelect(numValue);
                  // Update selectedPABValues to keep it in sync
                  setSelectedPABValues(numValue ? [numValue.toString()] : []);
                }}
                placeholder={activeTab === "sessions" ? "Select Achiever" : "Select Advisory Board Session"}
                options={pabs.map((pabItem) => ({
                  label: pabItem.achiever.fullName,
                  value: pabItem.pabSessionId.toString(),
                }))}
                className="w-full cursor-pointer"
              />

              {isLoading && (
                <p className="text-xs text-primary-600">Loading Personal Advisory Board session details...</p>
              )}
              {error && (
                <p className="text-xs text-red-600">
                  Error loading session details. Please try again.
                </p>
              )}
            </div>

            {/* Goal Segmented Control matching Figma exactly - Only show for goals tab with single PAB selected */}
            {activeTab === "goals" && currentGoals.length > 0 && selectedPABValues.length === 1 && (
              <div className="w-full">
                <div className="flex rounded bg-primary-50 p-1 h-6 overflow-x-auto hide-scrollbar">
                  {currentGoals.map((goal, index) => (
                    <button
                      key={goal.id}
                      onClick={() => setSelectedGoal(index)}
                      className={`flex-shrink-0 px-3 text-xs font-bold rounded transition-colors ${
                        selectedGoal === index
                          ? "bg-white text-gray-900 shadow-sm font-extrabold"
                          : "text-gray-600 hover:text-gray-900 font-normal"
                        }`}
                      style={{ fontSize: "12px", lineHeight: "16px" }}
                    >
                      Goal {index + 1}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Content based on active tab */}
            {activeTab === "goals" && (
              <div className="space-y-4">
                {selectedPABValues.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-sm text-gray-500">
                      Please select a Personal Advisory Board session to view goals
                    </p>
                  </div>
                ) : selectedPABValues.length > 1 ? (
                  <div className="text-center py-8">
                    <p className="text-sm text-gray-500">
                      Please select a single Personal Advisory Board session to view goals
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Goals view is available for one session at a time
                    </p>
                  </div>
                ) : isLoading ? (
                  <div className="text-center py-8">
                    <p className="text-sm text-gray-500">Loading goal details...</p>
                  </div>
                ) : error ? (
                  <div className="text-center py-8">
                    <p className="text-sm text-red-500">Error loading goal details</p>
                  </div>
                ) : currentGoal ? (
                  <>
                    <Heading as="h2" className="text-lg font-bold text-gray-900 leading-tight">
                      {currentGoal.title}
                    </Heading>

                    {/* Goal Summary Section */}
                    <div className="space-y-1">
                      <Text as="p" className="font-semibold text-gray-900 text-sm">Goal Summary</Text>
                      <Text as="p" className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">
                        <FormattedText text={currentGoal.description} />
                      </Text>

                      {/* Progress Bar */}
                      <div className="mt-2">
                        <div className="flex items-center justify-between mb-2">
                          <Text className="font-inter font-semibold text-sm text-text-dark">Progress</Text>
                          <Text className="font-inter font-semibold text-sm text-text-dark">
                            {currentGoal.progressPercentage}%
                          </Text>
                        </div>
                        <ProgressBar percentage={currentGoal.progressPercentage} height="sm" />
                      </div>
                    </div>

                    {/* Divider */}
                    <hr className="border-gray-200" />

                    {/* Timeline Section */}
                    <div className="space-y-1">
                      <Text as="p" className="font-semibold text-gray-900 text-sm">Timeline</Text>
                      <Text as="p" className="text-sm text-gray-600">
                        {formatDate(currentGoal.dueDate ?? null)}
                      </Text>
                    </div>

                    {/* Goal Status Section */}
                    <div className="space-y-1">
                      <Text as="p" className="font-semibold text-gray-900 text-sm">Status</Text>
                      <span
                        className={`inline-flex items-center h-6 rounded-full px-2.5 text-12 font-medium ${getGoalStatusColor(currentGoal.status)}`}
                      >
                        {getGoalStatusLabel(currentGoal.status)}
                      </span>
                    </div>

                    {/* Divider */}
                    <hr className="border-gray-200" />

                    {/* Key Tasks (OKTs) Section */}
                    <div className="space-y-3">
                      <Text as="p" className="font-semibold text-gray-900 text-sm">
                        Key Initiatives (OKIs) ({completedTasks}/{totalTasks})
                      </Text>
                      {currentGoal.tasks && currentGoal.tasks.length > 0 ? (
                        <div className="space-y-4">
                          {currentGoal.tasks.map((task) => (
                            <div
                              key={task.id}
                              className="flex items-start gap-3 p-3 bg-gray-50 rounded"
                            >
                              <div className="flex-1">
                                <Text as="p" className="text-sm text-gray-700">{task.title}</Text>
                                {task.description && (
                                  <Text as="p" className="text-xs text-gray-500 mt-1">{task.description}</Text>
                                )}
                                <div className="flex items-center gap-2 mt-1.5">
                                  <span
                                    className={`inline-flex items-center h-6 rounded-full px-2.5 text-12 font-medium ${getTaskStatusColor(task.status)}`}
                                  >
                                    {getTaskStatusLabel(task.status)}
                                  </span>
                                  {task.dueDate && (
                                    <Text as="span" className="text-xs text-gray-500">
                                      {formatDate(task.dueDate)}
                                    </Text>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <Text as="p" className="text-sm text-gray-500">No tasks available for this goal.</Text>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-sm text-gray-500">Select a Personal Advisory Board session to see the Goals</p>
                  </div>
                )}
              </div>
            )}

            {/* Sessions Tab Content - Hidden: sessions route is now available */}
            {false && activeTab === "sessions" && (
              <div className="space-y-6">
                {/* Upcoming Sessions */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-base font-bold text-gray-900">Upcoming Sessions</h2>
                  </div>

                  <div className="space-y-4">
                    {meetingsLoading ? (
                      <div className="text-center py-8">
                        <p className="text-sm text-gray-500">Loading sessions...</p>
                      </div>
                    ) : meetingsError ? (
                      <div className="text-center py-8">
                        <p className="text-sm text-red-500">Error loading sessions</p>
                      </div>
                    ) : (
                      (() => {
                        const filteredMeetings =
                          meetings?.filter(
                            (meeting) =>
                              selectedPABIds.length === 0 ||
                              selectedPABIds.includes(meeting.pabSessionId),
                          ) || [];
                        return filteredMeetings.length > 0 ? (
                          filteredMeetings.map((meeting) => {
                            // Get advisor participants for display
                            const advisorParticipants = meeting.participants.filter(
                              (p) => p.role === "ADVISOR",
                            );
                            const advisorNames = advisorParticipants.map((p) => p.fullName);

                            return (
                              <div
                                key={meeting.id}
                                className="flex items-center gap-3 p-3 bg-white border border-gray-200 rounded-md"
                              >
                                <div className="flex-1">
                                  <div className="space-y-3">
                                    <div>
                                      <h3 className="font-semibold text-gray-900 text-sm">
                                        {meeting.title}
                                      </h3>
                                      <p className="text-sm text-gray-600">
                                        {formatMeetingDateTime(meeting.startTime, meeting.endTime)}
                                      </p>
                                      {advisorNames.length > 0 && (
                                        <p className="text-xs text-gray-500">
                                          with {advisorNames.join(", ")}
                                        </p>
                                      )}
                                    </div>
                                    <div>
                                      <p className="text-xs text-gray-600">{meeting.description}</p>
                                    </div>
                                  </div>
                                </div>
                                {meeting.meetingUrl && (
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    className="px-3 py-1.5 text-xs font-medium bg-gray-600 hover:bg-gray-700"
                                    onClick={() => {
                                      const safeUrl = validateSafeUrl(meeting.meetingUrl);
                                      if (safeUrl) {
                                        window.open(safeUrl, "_blank");
                                      }
                                    }}
                                  >
                                    JOIN
                                  </Button>
                                )}
                              </div>
                            );
                          })
                        ) : (
                          <div className="text-center py-8">
                            <p className="text-sm text-gray-500">
                              {selectedPABIds.length > 0
                                ? "No upcoming sessions for selected achievers"
                                : "No upcoming sessions"}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              New sessions will appear here
                            </p>
                          </div>
                        );
                      })()
                    )}
                  </div>
                </div>

                {/* Past Sessions */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-base font-bold text-gray-900">Past Sessions</h2>
                  </div>

                  <div className="space-y-4">
                    {pastMeetingsLoading ? (
                      <div className="text-center py-8">
                        <p className="text-sm text-gray-500">Loading past sessions...</p>
                      </div>
                    ) : pastMeetingsError ? (
                      <div className="text-center py-8">
                        <p className="text-sm text-red-500">Error loading past sessions</p>
                      </div>
                    ) : (
                      (() => {
                        const filteredPastMeetings =
                          pastMeetings?.filter(
                            (meeting) =>
                              selectedPABIds.length === 0 ||
                              selectedPABIds.includes(meeting.pabSessionId),
                          ) || [];
                        return filteredPastMeetings.length > 0 ? (
                          filteredPastMeetings.map((meeting) => {
                            // Get advisor participants for display
                            const advisorParticipants = meeting.participants.filter(
                              (p) => p.role === "ADVISOR",
                            );
                            const advisorNames = advisorParticipants.map((p) => p.fullName);

                            return (
                              <div
                                key={meeting.id}
                                className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-md"
                              >
                                <div className="flex-1">
                                  <div className="space-y-3">
                                    <div>
                                      <h3 className="font-semibold text-gray-900 text-sm">
                                        {meeting.title}
                                      </h3>
                                      <p className="text-sm text-gray-600">
                                        {formatMeetingDateTime(meeting.startTime, meeting.endTime)}
                                      </p>
                                      {advisorNames.length > 0 && (
                                        <p className="text-xs text-gray-500">
                                          with {advisorNames.join(", ")}
                                        </p>
                                      )}
                                    </div>
                                    <div>
                                      <p className="text-xs text-gray-600">{meeting.description}</p>
                                    </div>
                                  </div>
                                </div>
                                <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded">
                                  Completed
                                </span>
                              </div>
                            );
                          })
                        ) : (
                          <div className="text-center py-8">
                            <p className="text-sm text-gray-500">
                              {selectedPABIds.length > 0
                                ? "No past sessions for selected achievers"
                                : "No past sessions"}
                            </p>
                          </div>
                        );
                      })()
                    )}
                  </div>
                </div>
              </div>
            )}
            {/* Messages Tab Content */}
            {activeTab === "messages" &&
              (selectedPABId ? (
                <div className="h-full">
                  <MessagesTab sessionId={selectedPABId} />
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-sm text-gray-500">Select a Personal Advisory Board session to see the Messages</p>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

AdvisorGoalsDrawer.displayName = "AdvisorGoalsDrawer";
